import React, { useState } from 'react';
import { breakLineTag } from '../../consts/MessageBreakLines';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { WhatsApp, Message, CheckCircle } from '@mui/icons-material';
import MessageProcessor from '../../utils/MessageProcessor';
import CustomButton from '../buttons/CustomButton';
import styles from './MessageTemplateSelector.module.css';

/**
 * Diálogo para escolher uma das variações de mensagem. A prévia mostra a
 * mensagem já montada (cabeçalho com o nome da empresa e a primeira linha
 * do texto). Com `viaWhatsAppWeb` os textos dizem que a mensagem abre pronta
 * no WhatsApp Web para a pessoa enviar; sem ele, que o sistema envia.
 */
const MessageTemplateSelector = ({
    open,
    title,
    templates,
    onSelect,
    onClose,
    appointmentData,
    patientData,
    resourceData,
    procedureData,
    companyName,
    viaWhatsAppWeb = false
}) => {
    const [selectedTemplate, setSelectedTemplate] = useState(0);

    const handleSelect = () => {
        onSelect(selectedTemplate);
        onClose();
    };

    // Processar mensagens para exibir sem placeholders
    const processedMessages = React.useMemo(() => {
        if (!templates || templates.length === 0) return [];

        // Se temos dados reais, usar eles; senão usar dados de exemplo
        const hasRealData = appointmentData && patientData && resourceData && procedureData;

        if (hasRealData) {
            return MessageProcessor.processMessages(
                templates,
                appointmentData,
                patientData,
                resourceData,
                procedureData,
                companyName
            );
        } else {
            // Usar dados de exemplo para preview
            const exampleData = MessageProcessor.getExampleData();
            return MessageProcessor.processMessages(
                templates,
                exampleData.appointmentData,
                exampleData.patientData,
                exampleData.resourceData,
                exampleData.procedureData,
                companyName || exampleData.companyName
            );
        }
    }, [templates, appointmentData, patientData, resourceData, procedureData, companyName]);

    const formatMessagePreview = (message) => {
        if (!message) return '';

        // Converter quebras de linha para exibição
        let formatted = message.replaceAll(breakLineTag, '\n');
        formatted = formatted.replaceAll('%0a', '\n');

        // Pegar as 2 primeiras linhas com texto: o cabeçalho ("*Empresa*") é
        // seguido de uma linha vazia, que não ajuda a distinguir as opções
        const lines = formatted.split('\n').filter((line) => line.trim() !== '');
        const firstTwoLines = lines.slice(0, 2).join('\n');

        // Se há mais de 2 linhas, adicionar "..."
        if (lines.length > 2) {
            return firstTwoLines + '...';
        }

        return firstTwoLines;
    };

    const subtitle = viaWhatsAppWeb
        ? 'Escolha uma das opções de mensagem abaixo. A mensagem abre pronta no WhatsApp Web para você enviar:'
        : 'Escolha uma das opções de mensagem abaixo. A mensagem será enviada exatamente como mostrada:';
    const confirmLabel = viaWhatsAppWeb ? 'Abrir no WhatsApp Web' : 'Enviar Mensagem';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: styles.dialogPaper
            }}
        >
            <DialogTitle className={styles.dialogTitle}>
                <div className={styles.titleContent}>
                    <WhatsApp className={styles.titleIcon} />
                    <span>{title}</span>
                </div>
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
                <div className={styles.subtitle}>
                    {subtitle}
                </div>
                <RadioGroup
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(Number(e.target.value))}
                    className={styles.templateList}
                >
                    {processedMessages.map((processedMessage, index) => (
                        <FormControlLabel
                            key={index}
                            value={index}
                            control={
                                <Radio
                                    className={styles.radioButton}
                                    checkedIcon={<CheckCircle />}
                                />
                            }
                            label={
                                <div className={styles.templateCard}>
                                    <div className={styles.templateNumber}>
                                        {index + 1}
                                    </div>
                                    <div className={styles.templatePreview}>
                                        {formatMessagePreview(processedMessage)}
                                    </div>
                                </div>
                            }
                            className={`${styles.templateItem} ${selectedTemplate === index ? styles.selected : ''}`}
                        />
                    ))}
                </RadioGroup>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
                <CustomButton
                    onClick={onClose}
                    variant="outlined"
                    style="secondary"
                    label="Cancelar"
                />
                <CustomButton
                    onClick={handleSelect}
                    variant="contained"
                    style="primary"
                    label={confirmLabel}
                    icon={<WhatsApp />}
                />
            </DialogActions>
        </Dialog>
    );
};

export default MessageTemplateSelector;